import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  AudioVideoObserver,
  ClientMetricReport,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatus,
  VideoSource,
  VideoTileState,
} from 'amazon-chime-sdk-js';
import { combineLatest, interval, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { AudioVideoConnectionMonitorSelectors } from 'src/app/features/audio-video-connection-monitor/store';
import {
  ExceptionType,
  NotificationsActions,
  NotificationType,
} from 'src/app/features/notifications';
import { PackagesSelectors } from 'src/app/features/packages';
import { RecordingStatus } from 'src/app/features/video-conference/chime/enums/recording-status-modals.enum';
import { AttendeeResponse } from 'src/app/features/video-conference/chime/models/AttendeeResponse';
import { MeetingResponse } from 'src/app/features/video-conference/chime/models/MeetingResponse';
import { ChimeActions, ChimeSelectors } from 'src/app/features/video-conference/chime/store';
import { VideoLayoutService } from 'src/app/features/video/services';
import { VideoActions } from 'src/app/features/video/store';
import { RootStoreState } from 'src/app/store';
import { v4 as uuid } from 'uuid';
import { Feature } from 'src/app/features/feature-management/models';
import { FeatureManagementService } from 'src/app/features/feature-management/services';
import { VideoLayout } from 'src/app/features/video/enums';
import { CustomChimeLogger } from 'src/app/features/video-conference/chime/loggers/custom-chime-logger';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { ParticipantVerificationSelectors } from 'src/app/features/participant-verification/store';
import { SpeechRecognitionActions } from 'src/app/features/speech';
import Timeout = NodeJS.Timeout;

@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
  standalone: false,
})
export class VideoComponent implements OnInit, OnDestroy {
  allSignersApproved = false;

  constructor(
    private readonly store: Store<RootStoreState.State>,
    private readonly videoLayoutService: VideoLayoutService,
    private readonly router: Router,
    private readonly featureManagementService: FeatureManagementService,
    private readonly actions$: Actions
  ) {
    // Print out Chime Logs to the console
    this.logger = new CustomChimeLogger('ChimeLogs', LogLevel.WARN, store);
    this.deviceController = new DefaultDeviceController(this.logger);

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd || event instanceof NavigationStart) {
        clearInterval(this.frameCaptureIntervalId);
        if (
          !(
            event.url.includes('signing-room') ||
            event.url.includes('unauthorized') ||
            event.url.includes('waiting-room')
          )
        ) {
          if (this.isChimeEnhancedLoggingEnabled) {
            this.store.dispatch(
              ChimeActions.SendWarningLogToLogger({
                payload: {
                  message: `Chime Meeting and Recording is now being stopped with incoming URL: ${event.url}`,
                },
              })
            );
          }
          this.store.dispatch(ChimeActions.StopMeetingAndRecording());
        }

        this.stopLocalMeetingSession();
        this.ngOnDestroy();
      }
    });
  }

  //Video Layout
  activeLayout: VideoLayout;
  availableLayouts = VideoLayout;
  numberOfActiveVideoStreams: number;

  activeCamera: MediaDeviceInfo;
  activeMicrophone: MediaDeviceInfo;

  onDestroyNotifier$ = new Subject();

  // Client Request Token
  clientRequestToken: any;

  // Meeting
  meetingSession: DefaultMeetingSession;

  // Attendee
  attendeeId: any;

  //AudioVideo Local
  hasLocalVideoStarted = false;
  isLocalVideoPaused = false;
  remoteVideoTiles: number[] = [];
  lowFramerateCount = 0;

  // AudioVideo Remote
  currentRemoteVideoSources: VideoSource[];

  // Recording
  isRecordingActive$: Observable<boolean>;
  showRecordingText = false;

  // Misc
  deviceController: DefaultDeviceController;
  logger: ConsoleLogger;
  observer: AudioVideoObserver;
  routerSubscription: Subscription;
  frameCaptureIntervalId: Timeout;

  isChimeMediaStatusCallbacksEnabled = false;

  // Chime audio monitoring
  private audioSignalLostAttendees = new Set<string>();
  private lastAudioPacketsReceived = 0;
  private consecutiveAudioPacketStallCount = 0;

  // Chime enhanced logging
  private isChimeEnhancedLoggingEnabled = false;

  // HTML Elements
  @ViewChild('localAudioElement') localAudioElementRef: ElementRef<HTMLAudioElement>;
  @ViewChild('localVideoElement') localVideoElementRef: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElementRef: ElementRef<HTMLCanvasElement>;

  async ngOnInit(): Promise<void> {
    this.isChimeMediaStatusCallbacksEnabled =
      this.featureManagementService.getIsFeatureEnabledWithCaching(
        Feature.ChimeMediaStatusCallbacks
      );

    this.isChimeEnhancedLoggingEnabled =
      this.featureManagementService.getIsFeatureEnabledWithCaching(Feature.ChimeEnhancedLogging);
    (this.logger as CustomChimeLogger).setEnhancedLoggingEnabled(this.isChimeEnhancedLoggingEnabled);

    this.store.dispatch(ChimeActions.GetMeeting());
    this.numberOfActiveVideoStreams = 0;

    this.store
      .select(AudioVideoConnectionMonitorSelectors.getSelectedVideoDevice)
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        filter<MediaDeviceInfo>(Boolean),
        tap((videoDevice: MediaDeviceInfo) => {
          this.activeCamera = videoDevice;
          if (this.hasLocalVideoStarted === true) {
            this.meetingSession.audioVideo.startVideoInput(this.activeCamera.deviceId);
          }
        })
      )
      .subscribe();

    this.isRecordingActive$ = this.store.pipe(
      select(ChimeSelectors.getRecordingStatus),
      filter(
        (recordingStatus) =>
          !!recordingStatus && recordingStatus !== RecordingStatus.Unknown
      ),
      map((recordingStatus) => {
        if (recordingStatus === RecordingStatus.InProgress) {
          this.store.dispatch(SpeechRecognitionActions.StartSpeechRecognition());
          return true;
        } else {
          this.store.dispatch(SpeechRecognitionActions.StopSpeechRecognition());
          return false;
        }
      })
    );

    combineLatest([
      this.store.pipe(select(ChimeSelectors.getMeetingDetails)),
      this.store.pipe(select(ChimeSelectors.getAttendeeDetails)),
      this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
    ])
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        filter(
          ([meetingDetails, attendee, activePackageGuid]) =>
            !!meetingDetails && !!attendee && !!activePackageGuid
        ),
        tap(async ([meetingDetails, attendee, activePackageGuid]) => {
          this.attendeeId = attendee.attendee?.attendeeId;
          if (
            this.meetingSession?.configuration?.meetingId !== meetingDetails?.meeting?.meetingId ||
            this.clientRequestToken !== activePackageGuid
          ) {
            this.clientRequestToken = activePackageGuid;
            this.initializeChime();
            await this.setupMeetingSession(meetingDetails, attendee);
          }
        })
      )
      .subscribe();

    this.store
      .pipe(
        select(ParticipantVerificationSelectors.getAllSignersApproved),
        distinctUntilChanged(),
        takeUntil(this.onDestroyNotifier$),
        tap((approved) => {
          this.allSignersApproved = approved;
          if (approved && !!this.meetingSession?.audioVideo) {
            this.store.dispatch(ChimeActions.GetRecordingStatus());
          }
        })
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(ChimeActions.StartRecordingComplete),
        take(1),
        takeUntil(this.onDestroyNotifier$),
        tap(() => this.longPollingGetMeetingStatus())
      )
      .subscribe();
  }

  async setupMeetingSession(meetingResponse: MeetingResponse, attendeeResponse: AttendeeResponse) {
    this.createNewMeetingSession(meetingResponse, attendeeResponse);
    if (this.meetingSession) {
      this.setupVideoObserver();
    }

    const videoInputQuality = {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 15 },
    };

    this.meetingSession.audioVideo.chooseVideoInputQuality(
      videoInputQuality.width.ideal,
      videoInputQuality.height.ideal,
      videoInputQuality.frameRate.ideal
    );

    await this.displayLocalVideo();
  }

  createNewMeetingSession(
    meetingResponse: MeetingResponse,
    attendeeResponse: AttendeeResponse
  ): void {
    const meetingConfiguration = new MeetingSessionConfiguration(meetingResponse, attendeeResponse);
    this.meetingSession = new DefaultMeetingSession(
      meetingConfiguration,
      this.logger,
      this.deviceController
    );
  }

  private initializeChime() {
    if (!this.clientRequestToken || !this.attendeeId) {
      this.store.dispatch(
        NotificationsActions.AddNotification({
          payload: {
            exception: new Error('Client Request Token or Attendee ID is missing'),
            notificationType: NotificationType.Error,
            id: uuid(),
            text: 'Failed to initialize chime',
            exceptionType: ExceptionType.CannotProceed,
          },
        })
      );
    }
  }

  public async displayLocalVideo() {
    try {
      await this.meetingSession.audioVideo.chooseAudioOutput(null);

      this.hasLocalVideoStarted = true;

      await this.meetingSession.audioVideo.startVideoInput(this.activeCamera.deviceId);

      this.meetingSession.audioVideo.startLocalVideoTile();

      this.store
        .select(PackageUsersSelectors.getIsActivePackageUserSigningAgent)
        .pipe(
          takeUntil(this.onDestroyNotifier$),
          filter((isSigningAgent) => isSigningAgent),
          tap(() => {
            this.captureFrameEveryMinute();
          })
        )
        .subscribe();

      this.startRecording();
    } catch (error) {
      this.store.dispatch(
        NotificationsActions.AddNotification({
          payload: {
            exception: error,
            notificationType: NotificationType.Error,
            id: uuid(),
            text: 'Error starting local video',
            exceptionType: ExceptionType.CannotProceed,
          },
        })
      );
    }
  }

  setupVideoObserver() {
    this.observer = {
      videoTileDidUpdate: async (tileState: VideoTileState) => {
        if (!tileState.localTile && !this.remoteVideoTiles.includes(tileState.tileId)) {
          this.remoteVideoTiles.push(tileState.tileId);
        }
        this.numberOfActiveVideoStreams = this.meetingSession.audioVideo
          .getAllVideoTiles()
          .filter((tile) => tile.state().active).length;

        this.updateVideoLayout(this.numberOfActiveVideoStreams);

        if (this.numberOfActiveVideoStreams >= 1) {
          this.store
            .select(AudioVideoConnectionMonitorSelectors.getSelectedAudioDevice)
            .pipe(
              takeUntil(this.onDestroyNotifier$),
              filter<MediaDeviceInfo>(Boolean),
              tap(async (audioDevice: MediaDeviceInfo) => {
                this.activeMicrophone = audioDevice;
                await this.meetingSession.audioVideo.startAudioInput(
                  this.activeMicrophone.deviceId
                );
              })
            )
            .subscribe();
        }

        if (this.localVideoElementRef && tileState.localTile) {
          const localVideoElement = this.localVideoElementRef.nativeElement;
          const localAudioElement = this.localAudioElementRef.nativeElement;
          this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, localVideoElement);
          await this.meetingSession.audioVideo.bindAudioElement(localAudioElement);
        }

        if (tileState.boundVideoElement === null && !tileState.localTile) {
          const videoElement = document.getElementById(
            `video-${tileState.tileId}`
          ) as HTMLVideoElement;

          const audioElement = document.getElementById(
            `audio-${tileState.tileId}`
          ) as HTMLAudioElement;

          if (videoElement === null || audioElement === null) {
            return;
          }

          this.meetingSession.audioVideo.bindVideoElement(tileState.tileId, videoElement);
          await this.meetingSession.audioVideo.bindAudioElement(audioElement);
        }
      },
      videoTileWasRemoved: (tileId: number) => {
        try {
          document.getElementById(`video-${tileId}`).remove();
          document.getElementById(`audio-${tileId}`).remove();
        } catch (_) {
          // Do nothing
        }
        this.remoteVideoTiles = this.remoteVideoTiles.filter((tid) => tid !== tileId);

        this.numberOfActiveVideoStreams = this.meetingSession.audioVideo
          .getAllVideoTiles()
          .filter((tile) => tile.state().active).length;

        this.updateVideoLayout(this.numberOfActiveVideoStreams);
      },
      audioVideoDidStart: () => {
        this.store.dispatch(
          VideoActions.SendVideoStreamConnected({ payload: { videoStreamId: this.attendeeId } })
        );
      },
      audioVideoDidStop: (sessionStatus: MeetingSessionStatus) => {
        this.store.dispatch(
          ChimeActions.SendErrorLogToLogger({
            payload: {
              message: `Audio/video session stopped - statusCode: ${sessionStatus.statusCode()}`,
            },
          })
        );
      },
      connectionDidBecomePoor: () => {
        this.store.dispatch(
          ChimeActions.SendWarningLogToLogger({
            payload: {
              message: 'Connection quality became poor - audio/video may be affected',
            },
          })
        );
      },
      connectionDidBecomeGood: () => {
        this.store.dispatch(
          ChimeActions.SendWarningLogToLogger({
            payload: {
              message: 'Connection quality recovered',
            },
          })
        );
      },
      metricsDidReceive: (clientMetricReport: ClientMetricReport) => {
        const metrics = clientMetricReport.getObservableMetrics();
        if (this.isChimeEnhancedLoggingEnabled && metrics.audioPacketsReceivedFractionLoss > 0.1) {
          this.store.dispatch(
            ChimeActions.SendWarningLogToLogger({
              payload: {
                message: `High audio packet loss detected: ${(metrics.audioPacketsReceivedFractionLoss * 100).toFixed(1)}%`,
              },
            })
          );
        }
        if (this.isChimeEnhancedLoggingEnabled) {
          this.store.dispatch(
            ChimeActions.SendWarningLogToLogger({
              payload: {
                message: `[VERBOSE] Audio metrics - packetLoss: ${((metrics.audioPacketsReceivedFractionLoss ?? 0) * 100).toFixed(1)}%, audioSpeakerDelay: ${metrics.audioSpeakerDelayMs ?? 'N/A'}ms, audioLevel: ${metrics.audioLevel ?? 'N/A'}`,
              },
            })
          );
        }
      },
      remoteVideoSourcesDidChange: (videoSources: VideoSource[]) => {
        if (this.currentRemoteVideoSources !== undefined) {
          this.currentRemoteVideoSources.forEach((currentVideoSource: VideoSource) => {
            if (
              !videoSources.some(
                (videoSource) =>
                  videoSource.attendee.attendeeId === currentVideoSource.attendee.attendeeId
              )
            ) {
              const disconnectedAttendeeId = currentVideoSource.attendee.attendeeId;
              this.store.dispatch(
                VideoActions.SendVideoStreamDestroyed({
                  payload: { videoStreamId: disconnectedAttendeeId },
                })
              );
              this.store.dispatch(
                ChimeActions.SendWarningLogToLogger({
                  payload: {
                    message: `Remote attendee video disconnected: ${disconnectedAttendeeId}`,
                  },
                })
              );
            }
          });
        }

        this.currentRemoteVideoSources = videoSources;
      },
    };

    this.meetingSession.audioVideo.addObserver(this.observer);
    this.meetingSession.audioVideo.start();

    this.subscribeToAudioMonitoring();
  }

  private subscribeToAudioMonitoring() {
    this.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      (attendeeId: string, present: boolean) => {
        if (present) {
          this.meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
            attendeeId,
            (_id: string, volume: number | null, muted: boolean | null, signalStrength: number | null) => {
              if (muted === false && signalStrength !== null && signalStrength === 0) {
                if (!this.audioSignalLostAttendees.has(attendeeId)) {
                  this.audioSignalLostAttendees.add(attendeeId);
                  this.store.dispatch(
                    ChimeActions.SendWarningLogToLogger({
                      payload: {
                        message: `Audio signal lost for attendee: ${attendeeId}`,
                      },
                    })
                  );
                }
              } else if (signalStrength !== null && signalStrength > 0 && this.audioSignalLostAttendees.has(attendeeId)) {
                this.audioSignalLostAttendees.delete(attendeeId);
                this.store.dispatch(
                  ChimeActions.SendWarningLogToLogger({
                    payload: {
                      message: `Audio signal restored for attendee: ${attendeeId}`,
                    },
                  })
                );
              }
            }
          );
        }
      }
    );
  }

  updateVideoLayout(newVideoCount: number) {
    this.activeLayout = this.videoLayoutService.calculateLayout(newVideoCount + 1);
  }

  startRecording() {
    this.store.dispatch(VideoActions.ShowStartingRecordingModal());

    setTimeout(() => {
      this.store.dispatch(ChimeActions.StartRecording());

      // TODO: Story 65327 - Remove ChimeMediaStatusCallbacks feature flag
      if (this.isChimeMediaStatusCallbacksEnabled) {
        this.store.dispatch(ChimeActions.GetRecordingStatus());
      }
    }, 2000);
  }

  showRecText() {
    this.showRecordingText = true;
  }

  hideRecText() {
    this.showRecordingText = false;
  }

  longPollingGetMeetingStatus() {
    interval(5000)
      .pipe(
        takeUntil(this.onDestroyNotifier$),
        tap(() => {
          if (!!this.meetingSession?.audioVideo && this.allSignersApproved) {
            // TODO: Story 65327 - Remove ChimeMediaStatusCallbacks feature flag
            if (!this.isChimeMediaStatusCallbacksEnabled) {
              this.store.dispatch(ChimeActions.GetRecordingStatus());
            }
            this.checkIsVideoStreaming();
          }
        })
      )
      .subscribe();
  }

  // Checks if video is sending out frames. If frame rate is 1 or less for 2 consecutive checks, video is considered paused.
  // This check is for mobile devices where video is paused when app is in background.
  // Also monitors audio RTP stats for packet stalls indicating audio drops.
  async checkIsVideoStreaming() {
    const statsPromise = this.meetingSession?.audioVideo?.getRTCPeerConnectionStats?.();
    if (!statsPromise || typeof statsPromise.then !== 'function') {
      return;
    }
    await statsPromise.then((stats) => {
      stats.forEach((report) => {
        if (report.type === 'outbound-rtp' && report.kind === 'video') {
          if (report.framesPerSecond <= 1 && !this.isLocalVideoPaused) {
            this.lowFramerateCount++;
          }

          if (report.framesPerSecond > 1 && this.isLocalVideoPaused) {
            this.store.dispatch(
              VideoActions.SendVideoStreamConnected({
                payload: { videoStreamId: this.attendeeId },
              })
            );
            this.store.dispatch(
              ChimeActions.SendWarningLogToLogger({
                payload: {
                  message: `Local video resumed for attendee: ${this.attendeeId}`,
                },
              })
            );
            this.lowFramerateCount = 0;
            this.isLocalVideoPaused = false;
          }

          if (this.lowFramerateCount >= 2) {
            this.store.dispatch(
              VideoActions.SendVideoStreamDestroyed({
                payload: { videoStreamId: this.attendeeId },
              })
            );
            this.store.dispatch(
              ChimeActions.SendWarningLogToLogger({
                payload: {
                  message: `Local video paused - low framerate for consecutive checks for attendee: ${this.attendeeId}`,
                },
              })
            );
            this.lowFramerateCount = 0;
            this.isLocalVideoPaused = true;
          }
        }

        if (this.isChimeEnhancedLoggingEnabled && report.type === 'inbound-rtp' && report.kind === 'audio') {
          const currentPacketsReceived = report.packetsReceived ?? 0;

          if (this.lastAudioPacketsReceived > 0 && currentPacketsReceived === this.lastAudioPacketsReceived) {
            this.consecutiveAudioPacketStallCount++;
          } else {
            if (this.consecutiveAudioPacketStallCount >= 2) {
              this.store.dispatch(
                ChimeActions.SendWarningLogToLogger({
                  payload: {
                    message: `Inbound audio packet stall recovered after ${this.consecutiveAudioPacketStallCount} consecutive checks`,
                  },
                })
              );
            }
            this.consecutiveAudioPacketStallCount = 0;
          }

          if (this.consecutiveAudioPacketStallCount === 2) {
            this.store.dispatch(
              ChimeActions.SendWarningLogToLogger({
                payload: {
                  message: `Inbound audio packets stalled - no new packets received for ${this.consecutiveAudioPacketStallCount} consecutive checks. packetsLost: ${report.packetsLost ?? 'N/A'}, jitter: ${report.jitter ?? 'N/A'}`,
                },
              })
            );
          }

          if (this.isChimeEnhancedLoggingEnabled) {
            this.store.dispatch(
              ChimeActions.SendWarningLogToLogger({
                payload: {
                  message: `[VERBOSE] Audio RTP stats - packetsReceived: ${currentPacketsReceived}, packetsLost: ${report.packetsLost ?? 'N/A'}, jitter: ${report.jitter ?? 'N/A'}, bytesReceived: ${report.bytesReceived ?? 'N/A'}`,
                },
              })
            );
          }

          this.lastAudioPacketsReceived = currentPacketsReceived;
        }
      });
    });
  }

  captureFrameEveryMinute() {
    this.frameCaptureIntervalId = setInterval(() => {
      this.captureFrame();
    }, 60000);
  }

  captureFrame() {
    const videoElement = this.localVideoElementRef.nativeElement;
    const canvasElement = this.canvasElementRef.nativeElement;
    const context = canvasElement.getContext('2d');

    if (context) {
      canvasElement.width = videoElement.videoWidth;
      canvasElement.height = videoElement.videoHeight;

      context.drawImage(videoElement, 0, 0, videoElement.videoWidth, videoElement.videoHeight);

      const imageDataUrl = canvasElement.toDataURL('image/webp');

      this.sendCapturedFrameToBackend(imageDataUrl);
    }
  }

  sendCapturedFrameToBackend(capturedFrame: string) {
    this.store.dispatch(
      ChimeActions.SendCapturedFrameToBackend({
        payload: { frame: capturedFrame },
      })
    );
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next(undefined);
    this.onDestroyNotifier$.complete();

    clearInterval(this.frameCaptureIntervalId);

    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  stopLocalMeetingSession() {
    if (this.meetingSession && this.meetingSession.audioVideo) {
      this.meetingSession.audioVideo.stop();
      this.meetingSession.audioVideo.stopLocalVideoTile();
      if (this.observer) {
        this.meetingSession.audioVideo.removeObserver(this.observer);
      }
    }
  }
}
